// TGCLOUD-1818 — the right input for the field type.
//
// Isolated because this is where the date fiddliness lives, and keeping it out
// of FilterBuilder is what stops the builder growing a date special-case.
//
// DateInput is the repo's picker wrapper: it takes `value` through
// `toLocalDate` (so a bare 'YYYY-MM-DD' string is safe to hand it — it will not
// parse as UTC midnight and render the previous day), it publishes a LOCAL
// Date, and everything cosmetic reaches the underlying TextField through
// `slotProps.textField`. The caller normalises what comes back with
// `toFilterDate`, which reads that Date by its local components. Reading it in
// UTC — via toISOString().slice(0,10), the obvious one-liner — moves the day at
// every negative offset, which is the bug behind TGCLOUD-1631/1632/1633.

import React from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import DateInput from 'src/components/UI/input/DateInput';
import { OPERATORS, isRange, isValueless } from './filterPredicates';

/**
 * @param {object} props
 * @param {{type:string, key:string}} props.field
 * @param {string} props.operator
 * @param {*} props.value
 * @param {(next:*) => void} props.onChange
 * @param {() => void} [props.onCommit]  Enter pressed in a text input
 * @param {Array<{value:string,label:string}>} [props.options] enum choices
 * @param {(s:string)=>string} props.t
 * @param {React.Ref} [props.inputRef]
 */
export default function FilterValueInput({
  field,
  operator,
  value,
  onChange,
  onCommit,
  options,
  t,
  inputRef
}) {
  // isEmpty / isNotEmpty take no value — rendering a disabled box would just
  // invite someone to type into it.
  if (isValueless(operator)) return null;

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onCommit?.();
    }
  };

  if (isRange(operator)) {
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <DateInput
          value={value?.from || null}
          onChange={(next) => onChange({ ...(value || {}), from: next })}
          slotProps={{ textField: { label: t('From'), size: 'small' } }}
        />
        <DateInput
          value={value?.to || null}
          onChange={(next) => onChange({ ...(value || {}), to: next })}
          slotProps={{ textField: { label: t('To'), size: 'small' } }}
        />
      </Box>
    );
  }

  if (field?.type === 'date') {
    // inYear takes a plain four-digit year, not a date. Handing it a
    // DatePicker would force the user to pick a day the record may not have —
    // which is the whole reason inYear exists.
    if (operator === OPERATORS.IN_YEAR) {
      return (
        <TextField
          inputRef={inputRef}
          label={t('Year')}
          value={value ?? ''}
          onChange={(e) =>
            onChange(e.target.value.replace(/\D/g, '').slice(0, 4))
          }
          onKeyDown={onKeyDown}
          size="small"
          fullWidth
          placeholder="1965"
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
        />
      );
    }
    return (
      <DateInput
        value={value || null}
        onChange={onChange}
        slotProps={{ textField: { label: t('Date'), size: 'small' } }}
      />
    );
  }

  if (field?.type === 'enum' && Array.isArray(options) && options.length) {
    const multi = operator === OPERATORS.IS_ONE_OF;
    const selected = multi
      ? String(value ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : value ?? '';
    return (
      <TextField
        inputRef={inputRef}
        select
        label={t('Value')}
        value={selected}
        onChange={(e) =>
          onChange(
            Array.isArray(e.target.value)
              ? e.target.value.join(',')
              : e.target.value
          )
        }
        size="small"
        fullWidth
        slotProps={{ select: { multiple: multi } }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField
      inputRef={inputRef}
      label={t('Value')}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      size="small"
      fullWidth
      placeholder={
        operator === OPERATORS.IS_ONE_OF
          ? t('Separate values with commas')
          : undefined
      }
      slotProps={{
        htmlInput: { type: field?.type === 'number' ? 'number' : 'text' }
      }}
    />
  );
}
